import { GrView } from "react-icons/gr";
import { useState } from "react";

import { SearchIcon, Calender, CountryIcon } from "../../assets/Icons";
import { IoChevronDownOutline, IoChevronUpSharp } from "react-icons/io5";
import countries from "../../data/countries.json";
import { UserTableProps, UserSearchFilterProps } from "./types";

export const UsersTable = ({
  users,
  onUserClick,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  total,
  perPage = 10,
}: UserTableProps) => {
  // Local pagination state (fallback if not using server pagination)
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Use props if available, otherwise use local state
  const page = currentPage || localCurrentPage;
  const pages = totalPages || Math.ceil(users.length / itemsPerPage);

  // For local pagination only
  const indexOfLastItem = localCurrentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = totalPages
    ? users
    : users.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page navigation
  const goToNextPage = () => {
    if (onPageChange) {
      // Server-side pagination
      if (page < pages) {
        onPageChange(page + 1);
      }
    } else {
      // Client-side pagination
      if (localCurrentPage < pages) {
        setLocalCurrentPage(localCurrentPage + 1);
      }
    }
  };

  const goToPreviousPage = () => {
    if (onPageChange) {
      // Server-side pagination
      if (page > 1) {
        onPageChange(page - 1);
      }
    } else {
      // Client-side pagination
      if (localCurrentPage > 1) {
        setLocalCurrentPage(localCurrentPage - 1);
      }
    }
  };
  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left ">
                <div className="flex items-center gap-1">
                  <input type="checkbox" className="mr-2" />
                  <p>Email</p>
                </div>
              </th>
              <th className="px-6 text-left ">UserName</th>
              <th className="px-6 text-left ">FirstName</th>
              <th className="px-6 text-left ">LastName</th>
              <th className="px-6 text-left">Country</th>
              <th className="px-6 text-left">View</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {loading ? (
              <tr className="border-b border-gray-200 h-[80px]">
                <td colSpan={6} className="px-6 text-center">
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : currentItems.length > 0 ? (
              currentItems.map((user) => (
                <tr
                  key={user.user_id}
                  className="border-b border-gray-200 h-[50px]"
                >
                  <td className="px-6 py-4 align-middle">
                    <div className="flex items-center gap-3">
                      <span>{user.avatar}</span>
                      <span>{user.email}</span>
                    </div>
                  </td>
                  <td className="px-6 ">{user.username || "N/A"}</td>
                  <td className="px-6 ">{user.firstname || "N/A"}</td>
                  <td className="px-6 ">{user.lastname || "N/A"}</td>
                  <td className="px-6">{user.country || "N/A"}</td>
                  <td onClick={() => onUserClick(user)} className="px-6">
                    <GrView />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={6} className="px-6 text-center">
                  No datas available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {page} of {pages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            page === pages || pages === 0 || loading
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          onClick={goToNextPage}
          disabled={page === pages || pages === 0 || loading}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const UserSearchFilter: React.FC<UserSearchFilterProps> = ({
  onFilterChange,
  filters,
  filterOptions,
  loading,
}) => {
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    onFilterChange(name, value);
  };
  return (
    <div className="flex flex-col bp:flex-row bp:items-center bp:justify-between mb-4 gap-2">
      <div className="relative">
        <input
          type="text"
          onChange={(e) => onFilterChange("search", e.target.value)}
          placeholder="Search for a user..."
          className="border p-2 pl-10 rounded-3xl w-[260px] h-[40px] bp:h-[48px] bp:w-[500px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A] outline-none"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative">
          <input
            type="text"
            placeholder="DD-MM-YY - DD-MM-YY"
            className="border p-2 pl-10 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[273px] rounded-3xl dark:bg-[#FFFFFF1A] dark:text-white dark:border-[#FFFFFF1A] outline-none"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Calender />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <select
            name="country"
            onChange={(e) => onFilterChange("country", e.target.value)}
            className="border p-2 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[100px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A] outline-none cursor-pointer"
          >
            <option value="" className="dark:bg-zinc-900 dark:text-white">
              Country
            </option>
            {filterOptions?.country_options?.map((country: string) => (
              <option
                key={country}
                value={country}
                className="dark:bg-zinc-900 dark:text-white"
              >
                {country}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center justify-between">
          <select
            name="status"
            onChange={(e) => onFilterChange("status", e.target.value)}
            className="border p-2 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[100px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A] outline-none cursor-pointer"
          >
            {filterOptions?.status_options?.map((status: string) => (
              <option
                key={status}
                value={status}
                className="dark:bg-zinc-900 dark:text-white"
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
